import { err, ok } from '@src/services/common/Result';
import { LogManager } from '@src/utility/Logger';
import { requestMissingFields } from '@src/utility/story';

import type { Result } from '@src/services/common/Result';
import type { Transcription } from '@src/types.ts/story';

const logger = LogManager.getLogger('service:CorrectedAudioSwap');

/**
 * PLAT-11226 — corrected-audio hot-swap, fetch side.
 *
 * Auphonic-corrected artifacts land under versioned `-corrected` S3 keys and
 * populate prefers them once present, so "swap" is simply re-populating the
 * transcription fields and getting freshly-signed corrected URLs. This
 * service fetches and validates; applying the result to observable state is
 * the calling hook's job (services stay MobX-free).
 */

/** True when a transcription's audio/waveform already point at corrected keys. */
export function isCorrectedTranscription(
  transcription:
    | Pick<Transcription, 'audio' | 'waveformData'>
    | undefined
    | null,
): boolean {
  return Boolean(
    transcription?.audio?.url?.includes('audio-only-corrected.mp3') ||
    transcription?.waveformData?.url?.includes('waveformdata-corrected.json'),
  );
}

/**
 * Reloading the words would discard edits, so it needs no unsaved edits and
 * no edited word (a word's `state` also marks edits already saved).
 */
export function canReloadWordsWithAudio(editor: {
  hasUnsavedEdits: boolean;
  transcript: ReadonlyArray<{ state?: string }>;
}): boolean {
  if (editor.hasUnsavedEdits) return false;
  return !editor.transcript.some(token => token.state != null);
}

/**
 * Re-populates the story's transcription fields, expecting corrected URLs.
 * Returns an error when the corrected artifacts are not yet visible — the
 * caller may simply wait for the next signal.
 */
export async function fetchCorrectedTranscription(
  storyId: string,
): Promise<Result<Transcription>> {
  try {
    const { transcription } = await requestMissingFields(storyId, [
      'transcription',
    ]);
    if (!transcription) {
      return err('NOT_FOUND', 'populate returned no transcription');
    }
    if (!isCorrectedTranscription(transcription)) {
      // Signal fired but populate still resolves raw keys (S3 list lag or a
      // partial write) — treat as not-ready rather than swapping raw-for-raw.
      return err(
        'PRECONDITION',
        'corrected artifacts not yet visible to populate',
      );
    }
    return ok(transcription);
  } catch (error) {
    logger.warn('failed to fetch corrected transcription', { storyId, error });
    return err('EXTERNAL', 'populate request failed');
  }
}
